import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { LocalizedProcessingBadge, useT, type Translations } from '@/shared/i18n'
import { ToolHero } from '@/shared/ui/ToolHero'

type OutputMime = 'image/jpeg' | 'image/png' | 'image/webp'
type EffectMode = 'blur' | 'pixelate'
type BlurErrors = Translations['blurImage']['errorsText']

interface Point {
  x: number
  y: number
}

interface EffectRect {
  id: string
  x: number
  y: number
  width: number
  height: number
}

interface LoadedImage {
  file: File
  url: string
  width: number
  height: number
  mime: OutputMime
}

interface BlurResult {
  blob: Blob
  url: string
  width: number
  height: number
  mime: OutputMime
}

interface DragState {
  rectId: string
  startPointer: Point
  currentPointer: Point
  pointerId: number
}

const UTILITY_KEY = 'blur-image'
const MAX_FILE_SIZE = 50 * 1024 * 1024
const MAX_PIXELS = 36_000_000
const MIN_RECT_SIZE = 0.015
const ALLOWED_TYPES: OutputMime[] = ['image/jpeg', 'image/png', 'image/webp']

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function fileMime(file: File): OutputMime | null {
  const mime = file.type.toLowerCase()
  if (ALLOWED_TYPES.includes(mime as OutputMime)) {
    return mime as OutputMime
  }

  const name = file.name.toLowerCase()
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg'
  if (name.endsWith('.png')) return 'image/png'
  if (name.endsWith('.webp')) return 'image/webp'
  return null
}

function pointerToUnit(event: React.PointerEvent<HTMLElement>, element: HTMLElement): Point {
  const bounds = element.getBoundingClientRect()
  return {
    x: clamp((event.clientX - bounds.left) / bounds.width, 0, 1),
    y: clamp((event.clientY - bounds.top) / bounds.height, 0, 1),
  }
}

function nextRectId(): string {
  return `zone-${Date.now()}-${Math.round(Math.random() * 100_000)}`
}

function rectFromPoints(id: string, start: Point, current: Point): EffectRect {
  const x = Math.min(start.x, current.x)
  const y = Math.min(start.y, current.y)
  const width = Math.abs(current.x - start.x)
  const height = Math.abs(current.y - start.y)

  return {
    id,
    x: clamp(x, 0, 1),
    y: clamp(y, 0, 1),
    width: clamp(width, 0, 1 - x),
    height: clamp(height, 0, 1 - y),
  }
}

function loadImage(src: string, errors: BlurErrors): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(errors.corrupt))
    img.src = src
  })
}

function blobFromCanvas(
  canvas: HTMLCanvasElement,
  mime: OutputMime,
  errors: BlurErrors,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error(errors.exportFailed))
          return
        }
        if (mime === 'image/webp' && blob.type !== 'image/webp') {
          reject(new Error(errors.unsupportedOutput))
          return
        }
        resolve(blob)
      },
      mime,
      mime === 'image/png' ? undefined : 0.95,
    )
  })
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function outputFilename(file: File, mime: OutputMime): string {
  const base = file.name.replace(/\.[^.]+$/, '') || 'image'
  const ext = mime === 'image/jpeg' ? 'jpg' : mime.replace('image/', '')
  return `${base}-blurred.${ext}`
}

function drawPixelatedArea(
  context: CanvasRenderingContext2D,
  image: HTMLImageElement,
  rect: EffectRect,
  imageWidth: number,
  imageHeight: number,
  strength: number,
): void {
  const sourceX = Math.round(rect.x * imageWidth)
  const sourceY = Math.round(rect.y * imageHeight)
  const sourceWidth = Math.max(1, Math.round(rect.width * imageWidth))
  const sourceHeight = Math.max(1, Math.round(rect.height * imageHeight))
  const blockSize = Math.max(4, Math.round(strength * 1.6))
  const smallWidth = Math.max(1, Math.ceil(sourceWidth / blockSize))
  const smallHeight = Math.max(1, Math.ceil(sourceHeight / blockSize))
  const pixelCanvas = document.createElement('canvas')
  pixelCanvas.width = smallWidth
  pixelCanvas.height = smallHeight
  const pixelContext = pixelCanvas.getContext('2d')

  if (!pixelContext) return

  pixelContext.imageSmoothingEnabled = false
  pixelContext.drawImage(
    image,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    smallWidth,
    smallHeight,
  )

  context.save()
  context.imageSmoothingEnabled = false
  context.drawImage(
    pixelCanvas,
    0,
    0,
    smallWidth,
    smallHeight,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
  )
  context.restore()
}

function drawBlurredArea(
  context: CanvasRenderingContext2D,
  image: HTMLImageElement,
  rect: EffectRect,
  imageWidth: number,
  imageHeight: number,
  strength: number,
): void {
  const sourceX = Math.round(rect.x * imageWidth)
  const sourceY = Math.round(rect.y * imageHeight)
  const sourceWidth = Math.max(1, Math.round(rect.width * imageWidth))
  const sourceHeight = Math.max(1, Math.round(rect.height * imageHeight))

  context.save()
  context.beginPath()
  context.rect(sourceX, sourceY, sourceWidth, sourceHeight)
  context.clip()
  context.filter = `blur(${strength}px)`
  context.drawImage(
    image,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
  )
  context.restore()
}

export function BlurImagePage() {
  const t = useT()
  const [image, setImage] = useState<LoadedImage | null>(null)
  const [rects, setRects] = useState<EffectRect[]>([])
  const [mode, setMode] = useState<EffectMode>('blur')
  const [strength, setStrength] = useState(14)
  const [result, setResult] = useState<BlurResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isPreviewing, setIsPreviewing] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [dropActive, setDropActive] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const dragRef = useRef<DragState | null>(null)
  const [dragPreview, setDragPreview] = useState<EffectRect | null>(null)

  const clearResult = useCallback(() => {
    setResult((previous) => {
      if (previous) {
        URL.revokeObjectURL(previous.url)
      }
      return null
    })
  }, [])

  useEffect(() => {
    return () => {
      if (image) {
        URL.revokeObjectURL(image.url)
      }
    }
  }, [image])

  useEffect(() => {
    return () => {
      if (result) {
        URL.revokeObjectURL(result.url)
      }
    }
  }, [result])

  const zonesLabel = useMemo(() => {
    if (rects.length === 0) return t.blurImage.noZones
    return `${t.blurImage.zones}: ${rects.length}`
  }, [rects.length, t.blurImage.noZones, t.blurImage.zones])

  const setLoadedImage = useCallback(
    async (file: File) => {
      setError(null)
      setRects([])
      setDragPreview(null)
      clearResult()

      const mime = fileMime(file)
      if (!mime) {
        setError(t.blurImage.errorsText.unsupported)
        toolError(UTILITY_KEY, 'unsupported_type')
        return
      }
      if (file.size > MAX_FILE_SIZE) {
        setError(t.blurImage.errorsText.tooLarge)
        toolError(UTILITY_KEY, 'too_large')
        return
      }

      const url = URL.createObjectURL(file)
      try {
        const decoded = await loadImage(url, t.blurImage.errorsText)
        const width = decoded.naturalWidth
        const height = decoded.naturalHeight
        if (width < 1 || height < 1 || width * height > MAX_PIXELS) {
          throw new Error(t.blurImage.errorsText.tooLargeImage)
        }

        const nextImage: LoadedImage = { file, url, width, height, mime }
        setImage((previous) => {
          if (previous) {
            URL.revokeObjectURL(previous.url)
          }
          return nextImage
        })
      } catch (err) {
        URL.revokeObjectURL(url)
        const message = err instanceof Error ? err.message : t.blurImage.errorsText.corrupt
        setError(message)
        toolError(
          UTILITY_KEY,
          message === t.blurImage.errorsText.tooLargeImage ? 'too_large_image' : 'corrupt_image',
        )
      }
    },
    [clearResult, t.blurImage.errorsText],
  )

  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      const [file] = Array.from(incoming)
      if (file) {
        void setLoadedImage(file)
      }
    },
    [setLoadedImage],
  )

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        addFiles(event.target.files)
        event.target.value = ''
      }
    },
    [addFiles],
  )

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDropActive(true)
  }, [])

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDropActive(false)
  }, [])

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      event.stopPropagation()
      setDropActive(false)
      if (event.dataTransfer.files.length > 0) {
        addFiles(event.dataTransfer.files)
      }
    },
    [addFiles],
  )

  const handleStagePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!image) return
      const pointer = pointerToUnit(event, event.currentTarget)
      const rectId = nextRectId()
      dragRef.current = {
        rectId,
        startPointer: pointer,
        currentPointer: pointer,
        pointerId: event.pointerId,
      }
      setDragPreview(rectFromPoints(rectId, pointer, pointer))
      event.currentTarget.setPointerCapture(event.pointerId)
    },
    [image],
  )

  const handlePointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return

    const pointer = pointerToUnit(event, event.currentTarget)
    drag.currentPointer = pointer
    setDragPreview(rectFromPoints(drag.rectId, drag.startPointer, pointer))
  }, [])

  const stopPointerDrag = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return

    const rect = rectFromPoints(drag.rectId, drag.startPointer, drag.currentPointer)
    if (rect.width >= MIN_RECT_SIZE && rect.height >= MIN_RECT_SIZE) {
      setRects((previous) => [...previous, rect])
    }
    dragRef.current = null
    setDragPreview(null)
    event.currentTarget.releasePointerCapture(event.pointerId)
  }, [])

  const renderToBlob = useCallback(async (): Promise<Blob> => {
    if (!image) {
      throw new Error(t.blurImage.errorsText.noImage)
    }
    if (rects.length === 0) {
      throw new Error(t.blurImage.errorsText.noZones)
    }

    const decoded = await loadImage(image.url, t.blurImage.errorsText)
    const canvas = document.createElement('canvas')
    canvas.width = image.width
    canvas.height = image.height
    const context = canvas.getContext('2d')

    if (!context) {
      throw new Error(t.blurImage.errorsText.canvas)
    }

    if (image.mime === 'image/jpeg') {
      context.fillStyle = '#FFFFFF'
      context.fillRect(0, 0, canvas.width, canvas.height)
    }

    context.drawImage(decoded, 0, 0, image.width, image.height)
    rects.forEach((rect) => {
      if (mode === 'pixelate') {
        drawPixelatedArea(context, decoded, rect, image.width, image.height, strength)
      } else {
        drawBlurredArea(context, decoded, rect, image.width, image.height, strength)
      }
    })

    return blobFromCanvas(canvas, image.mime, t.blurImage.errorsText)
  }, [image, mode, rects, strength, t.blurImage.errorsText])

  useEffect(() => {
    if (!image || rects.length === 0) {
      clearResult()
      setIsPreviewing(false)
      return
    }

    let cancelled = false
    setIsPreviewing(true)
    setError(null)
    const timer = window.setTimeout(() => {
      void renderToBlob()
        .then((blob) => {
          const url = URL.createObjectURL(blob)
          if (cancelled) {
            URL.revokeObjectURL(url)
            return
          }
          setResult((previous) => {
            if (previous) {
              URL.revokeObjectURL(previous.url)
            }
            return {
              blob,
              url,
              width: image.width,
              height: image.height,
              mime: image.mime,
            }
          })
        })
        .catch((err) => {
          if (!cancelled) {
            setError(err instanceof Error ? err.message : t.blurImage.errorsText.exportFailed)
          }
        })
        .finally(() => {
          if (!cancelled) {
            setIsPreviewing(false)
          }
        })
    }, 300)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [clearResult, image, rects.length, renderToBlob, t.blurImage.errorsText.exportFailed])

  const handleDownload = useCallback(async () => {
    if (!image) return

    const startTime = performance.now()
    toolInvoke(UTILITY_KEY, {
      mode,
      strength,
      zones: rects.length,
    })
    setIsExporting(true)
    setError(null)

    try {
      const blob = result?.blob ?? (await renderToBlob())
      downloadBlob(blob, outputFilename(image.file, image.mime))
      toolSuccess(UTILITY_KEY, Math.round(performance.now() - startTime), {
        mode,
        strength,
        zones: rects.length,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : t.blurImage.errorsText.exportFailed)
      toolError(UTILITY_KEY, 'export_failed', {
        mode,
        strength,
        zones: rects.length,
      })
    } finally {
      setIsExporting(false)
    }
  }, [image, mode, rects.length, renderToBlob, result, strength, t.blurImage.errorsText.exportFailed])

  return (
    <div className="mx-auto max-w-6xl">
      <ToolHero
        eyebrow={t.blurImage.eyebrow}
        title={t.blurImage.title}
        description={t.blurImage.description}
        chips={[t.blurImage.formats, t.common.maxFileSize]}
        collapsed={Boolean(image)}
      />

      <div className={cn('grid grid-cols-1 gap-6', image && 'lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start')}>
        <div className="min-w-0 space-y-6">
          {!image && (
            <Card
              className={cn('mx-auto max-w-3xl transition-colors', dropActive && 'border-primary bg-primary/5')}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <CardContent className="p-4 text-center sm:p-8">
                <div className="mx-auto mb-5 hidden h-16 w-16 place-items-center sm:grid rounded-md border bg-background text-sm font-semibold text-muted-foreground">
                  IMG
                </div>
                <p className="mb-2 hidden text-lg font-medium [@media(pointer:fine)]:block">{t.blurImage.dropTitle}</p>
                <p className="mx-auto mb-5 max-w-md text-sm leading-6 text-muted-foreground">
                  {t.blurImage.dropDescription}
                </p>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp"
                  className="hidden"
                  onChange={handleInputChange}
                />
                <Button onClick={() => inputRef.current?.click()}>
                  {t.blurImage.chooseFile}
                </Button>
              </CardContent>
            </Card>
          )}

          {image && (
            <Card className="overflow-hidden">
              <CardContent className="space-y-4 p-4 sm:p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="truncate text-lg font-semibold tracking-normal" title={image.file.name}>
                      {image.file.name}
                    </h2>
                    <div className="mt-2 hidden flex-wrap gap-2 sm:flex">
                      <span className="rounded-md border bg-background px-2 py-1 text-xs font-medium text-muted-foreground">
                        {image.mime.replace('image/', '').toUpperCase()}
                      </span>
                      <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
                        {formatSize(image.file.size)}
                      </span>
                      <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
                        {image.width} x {image.height}
                      </span>
                    </div>
                  </div>
                  <Button variant="outline" onClick={() => inputRef.current?.click()}>
                    {t.blurImage.replaceImage}
                  </Button>
                </div>

                <input
                  ref={inputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp"
                  className="hidden"
                  onChange={handleInputChange}
                />

                <div
                  className="relative mx-auto w-full max-w-4xl touch-none select-none overflow-hidden rounded-md border bg-muted"
                  style={{ aspectRatio: `${image.width} / ${image.height}` }}
                  onPointerDown={handleStagePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={stopPointerDrag}
                  onPointerCancel={stopPointerDrag}
                >
                  <img
                    src={image.url}
                    alt={image.file.name}
                    className="h-full w-full object-fill"
                    draggable={false}
                  />
                  {[...rects, ...(dragPreview ? [dragPreview] : [])].map((rect, index) => (
                    <div
                      key={rect.id}
                      className={cn(
                        'absolute border-2 border-white bg-primary/20 shadow-[0_0_0_1px_rgba(0,0,0,0.35)]',
                        index === rects.length && dragPreview && 'border-dashed',
                      )}
                      style={{
                        left: `${rect.x * 100}%`,
                        top: `${rect.y * 100}%`,
                        width: `${rect.width * 100}%`,
                        height: `${rect.height * 100}%`,
                      }}
                    />
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-2 sm:gap-4">
                  <PreviewPanel
                    label={t.blurImage.before}
                    src={image.url}
                    name={image.file.name}
                    size={formatSize(image.file.size)}
                    dimensions={`${image.width} x ${image.height}`}
                  />
                  <PreviewPanel
                    label={t.blurImage.after}
                    src={result?.url ?? image.url}
                    name={result ? outputFilename(image.file, result.mime) : t.blurImage.waitingPreview}
                    size={
                      result
                        ? formatSize(result.blob.size)
                        : isPreviewing
                          ? t.blurImage.previewUpdating
                          : t.blurImage.notReady
                    }
                    dimensions={`${result?.width ?? image.width} x ${result?.height ?? image.height}`}
                    muted={!result}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {error && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          )}
        </div>

        {image && (
          <aside className="min-w-0 space-y-4 lg:sticky lg:top-24">
            <Card>
              <CardContent className="space-y-5 p-5">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold tracking-normal">{t.blurImage.settings}</h2>
                  <LocalizedProcessingBadge variant="local" />
                </div>

                <div className="space-y-2">
                  <div className="text-sm font-medium">{t.blurImage.modeLabel}</div>
                  <div className="grid grid-cols-2 gap-2">
                    {t.blurImage.modes.map((item) => (
                      <button
                        key={item.key}
                        type="button"
                        className={cn(
                          'rounded-md border px-3 py-2 text-sm font-medium transition-colors',
                          mode === item.key
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'bg-background text-foreground hover:bg-muted',
                        )}
                        onClick={() => setMode(item.key as EffectMode)}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>

                <label className="block space-y-2">
                  <span className="flex items-center justify-between gap-3 text-sm font-medium">
                    <span>{t.blurImage.strength}</span>
                    <span className="tabular-nums text-muted-foreground">{strength}</span>
                  </span>
                  <input
                    type="range"
                    min={4}
                    max={32}
                    value={strength}
                    className="w-full accent-primary"
                    onChange={(event) => setStrength(Number(event.target.value))}
                  />
                </label>

                <div className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
                  {zonesLabel}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setRects((previous) => previous.slice(0, -1))}
                    disabled={rects.length === 0}
                  >
                    {t.blurImage.undoZone}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setRects([])}
                    disabled={rects.length === 0}
                  >
                    {t.blurImage.clearZones}
                  </Button>
                </div>

                <Button
                  className="w-full"
                  size="lg"
                  onClick={handleDownload}
                  disabled={isExporting || isPreviewing || rects.length === 0}
                >
                  {isExporting ? t.blurImage.exporting : t.blurImage.download}
                </Button>
              </CardContent>
            </Card>

            <p className="rounded-md border bg-card px-3 py-2 text-xs leading-5 text-muted-foreground">
              {t.blurImage.manualNote}
            </p>
            <p className="rounded-md border bg-card px-3 py-2 text-xs leading-5 text-muted-foreground">
              {t.blurImage.privacyNote}
            </p>
          </aside>
        )}
      </div>

      <section className="mt-12 grid gap-4 border-t pt-8 lg:grid-cols-3">
        {t.blurImage.faq.map((item) => (
          <div key={item.question}>
            <h2 className="text-base font-semibold tracking-normal">{item.question}</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              {item.answer}
            </p>
          </div>
        ))}
      </section>
    </div>
  )
}

function PreviewPanel({
  label,
  src,
  name,
  size,
  dimensions,
  muted = false,
}: {
  label: string
  src: string
  name: string
  size: string
  dimensions: string
  muted?: boolean
}) {
  return (
    <div className="overflow-hidden rounded-md border bg-background">
      <div className={cn('aspect-video bg-muted', muted && 'opacity-55')}>
        <img src={src} alt={name} className="h-full w-full object-contain" />
      </div>
      <div className="space-y-1 p-2 sm:space-y-3 sm:p-3">
        <div>
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="truncate text-sm font-medium" title={name}>
            {name}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="rounded-md border bg-card px-2 py-1 text-xs text-muted-foreground">
            {dimensions}
          </span>
          <span className="rounded-md border bg-card px-2 py-1 text-xs text-muted-foreground">
            {size}
          </span>
        </div>
      </div>
    </div>
  )
}
